import type { MonthlyUserBreakdown, RouteAssignment } from '@/types';
import { formatIsoDate, formatReportPeriodRange } from '@/utils/date';
import { computeRouteDurationHours } from '@/utils/routeMetrics';

const CSV_ROW_DELIMITER = '\r\n';

/** Escapes a single CSV field: wraps in quotes if it contains a comma, quote, or newline. */
const escapeCsvField = (value: string | number): string => {
  const str = String(value);
  if (/[",\r\n]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

const toCsvRow = (fields: (string | number)[]): string =>
  fields.map(escapeCsvField).join(',') + CSV_ROW_DELIMITER;

/**
 * Builds a CSV export of the selected month's per-driver breakdown for payroll (#11):
 * one summary row per driver (route count, total hours, total earnings),
 * followed by the underlying route list for each driver so payroll can
 * verify the totals against individual completed routes.
 */
export const buildMonthlyBreakdownCsv = (
  periodMonth: string,
  breakdown: MonthlyUserBreakdown[],
  assignmentsByUserId: Map<string, RouteAssignment[]>,
): string => {
  let csv = toCsvRow(['Report month', periodMonth]);
  csv += CSV_ROW_DELIMITER;

  csv += toCsvRow([
    'Driver',
    'Reporting period',
    'Route count',
    'Total hours',
    'Total earnings (DKK)',
  ]);
  for (const user of breakdown) {
    csv += toCsvRow([
      user.displayName,
      formatReportPeriodRange(user.paySchedule, periodMonth),
      user.routeCount,
      user.totalHours.toFixed(1),
      user.totalEarnings.toFixed(2),
    ]);
  }
  csv += CSV_ROW_DELIMITER;

  csv += toCsvRow(['Driver', 'Reporting period', 'Date', 'Route', 'Hours', 'Pay (DKK)']);
  for (const user of breakdown) {
    const userAssignments = assignmentsByUserId.get(user.userId) ?? [];
    for (const assignment of userAssignments) {
      csv += toCsvRow([
        user.displayName,
        formatReportPeriodRange(user.paySchedule, periodMonth),
        formatIsoDate(assignment.date),
        assignment.routeSnapshot.name,
        assignment.kind === 'extra'
          ? (assignment.extraDurationHours ?? assignment.routeSnapshot.extraDurationHours ?? 0)
          : computeRouteDurationHours(
              assignment.routeSnapshot.startTime,
              assignment.routeSnapshot.endTime,
            ).toFixed(1),
        assignment.routeSnapshot.payAmount.toFixed(2),
      ]);
    }
  }

  return csv;
};

/** Triggers a browser download of `content` as a file named `filename`. */
export const downloadTextFile = (filename: string, content: string, mimeType: string): void => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
